import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableSortLabel,
  Tooltip,
  useTheme,
  LinearProgress,
} from '@mui/material';
import {
  Search,
  Refresh,
  FilterList,
  Visibility,
  ShoppingCart,
  Business,
  DateRange,
  Close,
  ExpandMore,
  ExpandLess,
  PictureAsPdf,
  Download,
  Edit,
  GridView,
  TrendingUp,
  VerifiedUser,
  CheckCircle,
  WarningAmber,
} from '@mui/icons-material';
import axios from '../api/client';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
// BRSyncButton removed — sync is now in the persistent AppHeader (App.js)

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8004';

const fmtDate = (raw) => {
  if (!raw) return '—';
  try {
    const d = new Date(raw);
    if (isNaN(d.getTime())) return String(raw);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return String(raw);
  }
};


const VENDOR_SELECT_TO_API = {
  'american-eagle': 'american_eagle',
  chico: 'chico',
  freepeople: 'freepeople',
  'lane-bryant': 'lane_bryant',
  mast: 'mast',
  loft: 'loft',
  talbots: 'talbots',
};
const SUB_VENDOR_TAG = {
  // snake_case keys (legacy, pre-migration rows)
  american_eagle_mens: { label: 'AEM',  full: 'American Eagle Men',     color: '#a01025' },
  aerie:               { label: 'AR',   full: 'Aerie',                  color: '#e85d8a' },
  pink:                { label: 'PK',   full: 'Pink',                   color: '#db2777' },
  loft_outlet:         { label: 'LFO',  full: 'Ann Taylor Loft Outlet', color: '#92400e' },
  ann_taylor:          { label: 'AT',   full: 'Ann Taylor',             color: '#78350f' },
  ann_taylor_factory:  { label: 'ATF',  full: 'Ann Taylor Factory',     color: '#6b3a0f' },
  // customerCode keys (post-migration rows)
  BAM:  { label: 'AEM',  full: 'American Eagle Men',     color: '#a01025' },
  BAR:  { label: 'AR',   full: 'Aerie',                  color: '#e85d8a' },
  BVP:  { label: 'PK',   full: 'Pink',                   color: '#db2777' },
  BLFO: { label: 'LFO',  full: 'Ann Taylor Loft Outlet', color: '#92400e' },
  BAT:  { label: 'AT',   full: 'Ann Taylor',             color: '#78350f' },
  BATF: { label: 'ATF',  full: 'Ann Taylor Factory',     color: '#6b3a0f' },
};

const API_VENDOR_TO_SELECT = {
  ...Object.fromEntries(Object.entries(VENDOR_SELECT_TO_API).map(([k, v]) => [v, k])),
  // snake_case sub-vendors (legacy)
  american_eagle_mens: 'american-eagle',
  aerie:               'american-eagle',
  pink:                'mast',
  loft_outlet:         'loft',
  ann_taylor:          'loft',
  ann_taylor_factory:  'loft',
  // customerCode mappings (post-migration)
  BAE:  'american-eagle',
  BAM:  'american-eagle',
  BAR:  'american-eagle',
  BC:   'chico',
  BLB:  'lane-bryant',
  BFP:  'freepeople',
  BTL:  'talbots',
  BVS:  'mast',
  BVP:  'mast',
  BLF:  'loft',
  BLFO: 'loft',
  BAT:  'loft',
  BATF: 'loft',
};

const VENDOR_COLORS = {
  'american-eagle': '#c41230',
  chico: '#7c3aed',
  freepeople: '#d97706',
  'lane-bryant': '#1d4ed8',
  mast: '#059669',
  loft: '#b45309',
  talbots: '#be123c',
};

const STATUS_CONFIG = {
  draft: { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.3)', label: 'Draft' },
  pending: { color: '#06b6d4', bg: 'rgba(6,182,212,0.12)', border: 'rgba(6,182,212,0.3)', label: 'Pending' },
  accepted: { color: '#10b981', bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.3)', label: 'Accepted' },
  rejected: { color: '#ef4444', bg: 'rgba(239,68,68,0.12)', border: 'rgba(239,68,68,0.3)', label: 'Rejected' },
};

const vendors = [
  { value: 'american-eagle', label: 'American Eagle', abbr: 'AE' },
  { value: 'chico', label: "Chico's", abbr: 'CH' },
  { value: 'freepeople', label: 'Free People', abbr: 'FP' },
  { value: 'lane-bryant', label: 'Lane Bryant', abbr: 'LB' },
  { value: 'mast', label: 'MAST', abbr: 'MA' },
  { value: 'loft', label: 'LOFT', abbr: 'LO' },
  { value: 'talbots', label: 'Talbots', abbr: 'TA' },
];

const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  return (
    <Box
      sx={{
        p: { xs: 2, sm: 2.5 },
        borderRadius: '14px',
        backgroundColor: isDark ? theme.palette.background.paper : '#fff',
        border: `1px solid ${isDark ? 'rgba(148,163,184,0.09)' : 'rgba(15,23,42,0.08)'}`,
        position: 'relative',
        overflow: 'hidden',
        height: '100%',
        transition: 'transform 0.18s ease, box-shadow 0.18s ease',
        cursor: 'default',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: isDark
            ? `0 12px 32px rgba(0,0,0,0.55), 0 0 0 1px ${color}30`
            : `0 12px 32px rgba(0,0,0,0.1), 0 0 0 1px ${color}20`,
        },
      }}
    >
      {/* Glow blob */}
      <Box
        sx={{
          position: 'absolute',
          top: -20,
          right: -20,
          width: 90,
          height: 90,
          borderRadius: '50%',
          backgroundColor: color,
          opacity: isDark ? 0.08 : 0.07,
          filter: 'blur(24px)',
          pointerEvents: 'none',
        }}
      />

      {/* Icon badge */}
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: '11px',
          background: `linear-gradient(135deg, ${color}22, ${color}10)`,
          border: `1px solid ${color}30`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          position: 'relative',
        }}
      >
        <Icon sx={{ fontSize: 20, color }} />
      </Box>

      {/* Content */}
      <Box sx={{ position: 'relative' }}>
        <Typography
          sx={{
            fontSize: '0.68rem',
            fontWeight: 700,
            letterSpacing: '0.07em',
            textTransform: 'uppercase',
            color: isDark ? 'rgba(148,163,184,0.7)' : 'rgba(100,116,139,0.8)',
            mb: 0.75,
            lineHeight: 1,
          }}
        >
          {title}
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: '1.5rem', sm: '1.875rem' },
            fontWeight: 800,
            fontFamily: '"DM Sans", sans-serif',
            letterSpacing: '-0.025em',
            color: isDark ? '#f1f5f9' : '#0f172a',
            lineHeight: 1.1,
            mb: subtitle ? 0.75 : 0,
            wordBreak: 'break-word',
          }}
        >
          {value ?? '—'}
        </Typography>

        {subtitle && (
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: isDark ? 'rgba(148,163,184,0.6)' : 'rgba(100,116,139,0.7)',
              lineHeight: 1.3,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const VendorBadge = ({ vendor }) => {
  const subTag = SUB_VENDOR_TAG[vendor] || null;
  const selectVal = API_VENDOR_TO_SELECT[vendor] || '';
  const vendorObj = vendors.find((v) => v.value === selectVal);

  // Sub-vendors: use sub-vendor identity as the primary display.
  // Parent vendors (no subTag): fall back to the parent label/color.
  const displayLabel = subTag ? subTag.full : (vendorObj?.label || vendor);
  const displayAbbr  = subTag ? subTag.label : (vendorObj?.abbr || vendor?.slice(0, 2).toUpperCase());
  const displayColor = subTag ? subTag.color : (VENDOR_COLORS[selectVal] || '#6366f1');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: '6px',
          backgroundColor: `${displayColor}20`,
          border: `1px solid ${displayColor}35`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, color: displayColor, fontFamily: '"DM Sans", sans-serif' }}>
          {displayAbbr}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 500, lineHeight: 1.2 }}>
        {displayLabel}
      </Typography>
    </Box>
  );
};

const StatusChip = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 1,
        py: 0.375,
        borderRadius: '6px',
        fontSize: '0.7rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        color: cfg.color,
        backgroundColor: cfg.bg,
        border: `1px solid ${cfg.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {cfg.label}
    </Box>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [stats, setStats] = useState(null);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedVendor, setSelectedVendor] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('accepted');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('id');
  const [order, setOrder] = useState('desc');
  const [filterDateFrom, setFilterDateFrom] = useState(null);
  const [filterDateTo, setFilterDateTo] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [pdfLoading, setPdfLoading] = useState(false);
  const [selectedPoId, setSelectedPoId] = useState(null);

  // ── Bamboo Rose Validation state ──────────────────────────────────────────
  const [validateDialogOpen, setValidateDialogOpen] = useState(false);
  const [validateLoading, setValidateLoading] = useState(false);
  const [validateData, setValidateData] = useState(null);
  const [validatePoId, setValidatePoId] = useState(null);
  // accepted[key] = true/false — key = "header:field" or "line:lid:field"
  const [accepted, setAccepted] = useState({});
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  // BR sync is handled by the shared <BRSyncButton /> component in the header

  const handleValidate = async (poId) => {
    setValidatePoId(poId);
    setValidateDialogOpen(true);
    setValidateLoading(true);
    setValidateData(null);
    setAccepted({});
    setApplyResult(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/bamboo-rose/verify/${poId}`);
      setValidateData(res.data);
      // Pre-accept all variances
      const init = {};
      (res.data.header_variances || []).forEach(v => { init[`header:${v.field}`] = true; });
      (res.data.line_item_variances || []).forEach(row => {
        row.variances.forEach(v => { init[`line:${row.line_item_id}:${v.field}`] = true; });
      });
      setAccepted(init);
    } catch (err) {
      setValidateData({ error: err.response?.data?.detail || err.message });
    } finally {
      setValidateLoading(false);
    }
  };

  const handleApplyAccepted = async () => {
    if (!validatePoId || !validateData) return;
    setApplying(true);
    const header_overrides = (validateData.header_variances || [])
      .filter(v => accepted[`header:${v.field}`])
      .map(v => ({ field: v.field, br_value: v.br_value }));
    const line_item_overrides = [];
    (validateData.line_item_variances || []).forEach(row => {
      row.variances.forEach(v => {
        if (accepted[`line:${row.line_item_id}:${v.field}`]) {
          line_item_overrides.push({ line_item_id: row.line_item_id, field: v.field, br_value: v.br_value });
        }
      });
    });
    try {
      const res = await axios.post(`${API_BASE_URL}/api/bamboo-rose/apply/${validatePoId}`, {
        header_overrides, line_item_overrides,
      });
      setApplyResult({ success: true, ...res.data });
      fetchData();  // refresh table
    } catch (err) {
      setApplyResult({ success: false, message: err.response?.data?.detail || err.message });
    } finally {
      setApplying(false);
    }
  };

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/purchase-orders/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const params = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        sort_by: orderBy,
        sort_order: order,
      };
      if (selectedVendor) params.vendor = VENDOR_SELECT_TO_API[selectedVendor] || selectedVendor;
      if (selectedStatus) params.status = selectedStatus;
      if (searchTerm) params.search = searchTerm;

      try {
        const response = await axios.get(`${API_BASE_URL}/api/purchase-orders/`, { params });
        setData(response.data.data || []);
        setTotal(response.data.total || 0);
      } catch (firstErr) {
        const fallbackParams = { limit: rowsPerPage, offset: page * rowsPerPage, sort_by: orderBy, sort_order: order };
        if (searchTerm) fallbackParams.search = searchTerm;
        const fallback = await axios.get(`${API_BASE_URL}/api/purchase-orders/`, { params: fallbackParams });
        setData(fallback.data.data || []);
        setTotal(fallback.data.total || 0);
        const detail = firstErr?.response?.data?.detail;
        setError(detail ? `Vendor filter failed: ${detail}` : 'Vendor filter failed; showing unfiltered results.');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail ? `Failed to fetch: ${detail}` : 'Failed to fetch purchase orders');
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); fetchData(); }, [selectedVendor, selectedStatus, page, rowsPerPage, orderBy, order]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 0) fetchData();
      else setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleViewPDF = async (poId) => {
    try {
      setPdfLoading(true);
      setSelectedPoId(poId);
      const response = await axios.get(`${API_BASE_URL}/api/purchase-orders/${poId}/document`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      setPdfUrl(window.URL.createObjectURL(blob));
      setPdfModalOpen(true);
    } catch (err) {
      setError('Failed to load PDF document');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadPDF = async (poId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/purchase-orders/${poId}/document?download=true`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `PO_${poId}_document.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download PDF');
    }
  };

  const handleClosePdfModal = () => {
    if (pdfUrl && String(pdfUrl).startsWith('blob:')) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setPdfModalOpen(false);
    setPdfUrl('');
    setSelectedPoId(null);
  };

  const handleViewAnnotatedPDF = async (poId) => {
    try {
      setPdfLoading(true);
      setSelectedPoId(poId);
      const response = await axios.get(`${API_BASE_URL}/api/purchase-orders/${poId}/annotated-download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      setPdfUrl(window.URL.createObjectURL(blob));
      setPdfModalOpen(true);
    } catch (err) {
      setError('Failed to load annotated PDF. Coordinates may not exist for this record.');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleDownloadAnnotatedPDF = async (poId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/purchase-orders/${poId}/annotated-download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'];
      link.download = cd ? cd.split('filename=')[1]?.replaceAll('"', '') : `PO_${poId}_Annotated.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download annotated PDF.');
    }
  };

  const handleDownloadExcel = (poId) => {
    const url = `${API_BASE_URL}/api/purchase-orders/${poId}/excel-download`;
    const link = document.createElement('a');
    link.href = url;
    link.download = `PO_${poId}_Extracted_Data.xlsx`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleRowExpansion = (poId) => {
    const next = new Set(expandedRows);
    next.has(poId) ? next.delete(poId) : next.add(poId);
    setExpandedRows(next);
  };

  const clearFilters = () => {
    setSelectedVendor('');
    setSelectedStatus('review');
    setSearchTerm('');
    setFilterDateFrom(null);
    setFilterDateTo(null);
    setPage(0);
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    return String(value);
  };

  const getLineItemHeaders = (lineItems) => {
    if (!lineItems || lineItems.length === 0) return [];
    const allKeys = new Set();
    lineItems.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (key !== 'id' && key !== 'purchase_order_id' && key !== 'item_coordinates') allKeys.add(key);
      });
    });
    return Array.from(allKeys).sort().map((key) => ({
      key,
      label: key.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    }));
  };

  if (statsLoading && loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress size={48} thickness={3} sx={{ color: '#6366f1' }} />
        <Typography sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.875rem' }}>Loading dashboard…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Page header */}
      <Box sx={{ mb: { xs: 3, sm: 4 }, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.025em',
              mb: 0.75,
              fontSize: { xs: '1.5rem', sm: '1.875rem' },
              background: isDark
                ? 'linear-gradient(135deg, #e2e8f0 0%, #94a3b8 100%)'
                : 'linear-gradient(135deg, #0f172a 0%, #475569 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Dashboard
          </Typography>
          <Typography sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
            Track and manage all incoming Purchase Orders across customers.
          </Typography>
        </Box>

        {/* Sync BR is in the persistent AppHeader — nothing needed here */}

      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stat cards */}
      <Grid container spacing={{ xs: 1.5, sm: 2 }} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Total Records"
            value={stats?.total_records != null ? stats.total_records.toLocaleString() : '—'}
            subtitle={`Across ${stats?.vendor_breakdown?.length ?? 0} customer${(stats?.vendor_breakdown?.length ?? 0) !== 1 ? 's' : ''}`}
            icon={ShoppingCart}
            color="#6366f1"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Active Customers"
            value={stats?.vendor_breakdown?.length ?? '—'}
            subtitle="Parsers configured"
            icon={Business}
            color="#06b6d4"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Earliest Order"
            value={fmtDate(stats?.date_range?.earliest)}
            subtitle={stats?.date_range?.latest ? `Latest: ${fmtDate(stats.date_range.latest)}` : 'No date data'}
            icon={DateRange}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <StatCard
            title="Data Health"
            value="Healthy"
            subtitle="All systems operational"
            icon={TrendingUp}
            color="#f59e0b"
          />
        </Grid>
      </Grid>


      {/* Filters */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 2.5,
          borderRadius: '12px',
          backgroundColor: isDark ? theme.palette.background.paper : '#fff',
          border: `1px solid ${isDark ? 'rgba(148,163,184,0.08)' : 'rgba(15,23,42,0.07)'}`,
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          {/* Row 1: Search + action buttons */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search PO number, style, customer…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 16, color: isDark ? '#94a3b8' : '#64748b' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: '1 1 220px', minWidth: 180 }}
            />
            <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
              <Button
                size="small"
                startIcon={<Refresh sx={{ fontSize: 15 }} />}
                onClick={fetchData}
                variant="outlined"
                sx={{
                  borderColor: isDark ? 'rgba(148,163,184,0.2)' : 'rgba(15,23,42,0.12)',
                  color: isDark ? '#94a3b8' : '#64748b',
                  whiteSpace: 'nowrap',
                  '&:hover': { borderColor: '#6366f1', color: '#6366f1', backgroundColor: 'rgba(99,102,241,0.05)' },
                }}
              >
                Refresh
              </Button>
              <Button
                size="small"
                startIcon={<FilterList sx={{ fontSize: 15 }} />}
                onClick={clearFilters}
                variant="outlined"
                sx={{
                  borderColor: isDark ? 'rgba(148,163,184,0.2)' : 'rgba(15,23,42,0.12)',
                  color: isDark ? '#94a3b8' : '#64748b',
                  whiteSpace: 'nowrap',
                  '&:hover': { borderColor: '#ef4444', color: '#ef4444', backgroundColor: 'rgba(239,68,68,0.05)' },
                }}
              >
                Clear
              </Button>
            </Box>
          </Box>

          {/* Row 2: Dropdowns + date pickers */}
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 160, flex: '1 1 160px' }}>
              <InputLabel>Customer</InputLabel>
              <Select value={selectedVendor} label="Customer" onChange={(e) => setSelectedVendor(e.target.value)}>
                <MenuItem value="">All Customers</MenuItem>
                {vendors.map((v) => (
                  <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160, flex: '1 1 160px' }}>
              <InputLabel>Status</InputLabel>
              <Select value={selectedStatus} label="Status" onChange={(e) => setSelectedStatus(e.target.value)}>
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="review">Needs Review</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="accepted">Accepted</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <DatePicker
                label="From Date"
                value={filterDateFrom}
                onChange={setFilterDateFrom}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Box>
            <Box sx={{ flex: '1 1 150px', minWidth: 150 }}>
              <DatePicker
                label="To Date"
                value={filterDateTo}
                onChange={setFilterDateTo}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Box>
          </Box>
        </LocalizationProvider>
      </Box>

      {!loading && !statsLoading && total === 0 && (
        <Alert severity={stats && stats.total_records > 0 ? 'info' : 'warning'} sx={{ mb: 2 }}>
          {stats && stats.total_records > 0
            ? 'No rows match your current filters. Try clearing filters or changing the customer/status.'
            : 'The database has no purchase orders yet. Upload PDFs to get started.'}
        </Alert>
      )}

      {/* Results header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography sx={{ fontSize: '0.8125rem', color: isDark ? '#94a3b8' : '#64748b' }}>
          {loading ? 'Loading…' : `${total.toLocaleString()} record${total !== 1 ? 's' : ''}`}
        </Typography>
      </Box>

      {/* Data table */}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ width: '100%', overflowX: 'auto', borderRadius: '12px' }}
      >
        {loading && <LinearProgress sx={{ '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #6366f1, #818cf8)' } }} />}
        <Table sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 40, minWidth: 40, px: 1 }} />
              <TableCell sx={{ width: 68, display: { xs: 'none', sm: 'table-cell' } }}>
                <TableSortLabel active={orderBy === 'id'} direction={orderBy === 'id' ? order : 'asc'} onClick={() => handleSort('id')}>
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 140 }}>
                <TableSortLabel active={orderBy === 'vendor'} direction={orderBy === 'vendor' ? order : 'asc'} onClick={() => handleSort('vendor')}>
                  Customer
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 120 }}>
                <TableSortLabel active={orderBy === 'customer_po_number'} direction={orderBy === 'customer_po_number' ? order : 'asc'} onClick={() => handleSort('customer_po_number')}>
                  PO Number
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ minWidth: 88, display: { xs: 'none', sm: 'table-cell' } }}>Status</TableCell>
              <TableCell sx={{ minWidth: 120, display: { xs: 'none', md: 'table-cell' } }}>Order Date</TableCell>
              <TableCell sx={{ minWidth: 120, display: { xs: 'none', lg: 'table-cell' } }}>Version Date</TableCell>
              <TableCell sx={{ minWidth: 150, display: { xs: 'none', lg: 'table-cell' } }}>Ship To</TableCell>
              <TableCell sx={{ minWidth: 120, display: { xs: 'none', xl: 'table-cell' } }}>Customer Name #</TableCell>
              <TableCell sx={{ minWidth: 150 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 6 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                    <GridView sx={{ fontSize: 40, color: isDark ? 'rgba(148,163,184,0.2)' : 'rgba(100,116,139,0.2)' }} />
                    <Typography sx={{ fontSize: '0.875rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                      No purchase orders found
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              data.map((po) => {
                const isExpanded = expandedRows.has(po.id);
                const lineItems = po.line_items || [];
                const lineItemHeaders = getLineItemHeaders(lineItems);
                return (
                  <React.Fragment key={po.id}>
                    <TableRow
                      sx={{
                        '&:last-child td': { borderBottom: 0 },
                        backgroundColor: isExpanded
                          ? isDark ? 'rgba(99,102,241,0.04)' : 'rgba(79,70,229,0.02)'
                          : 'transparent',
                      }}
                    >
                      <TableCell sx={{ py: 1, px: 1 }}>
                        {lineItems.length > 0 && (
                          <IconButton size="small" onClick={() => toggleRowExpansion(po.id)} sx={{ width: 28, height: 28 }}>
                            {isExpanded ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: isDark ? '#94a3b8' : '#64748b' }}>
                          #{po.id}
                        </Typography>
                      </TableCell>
                      <TableCell><VendorBadge vendor={po.vendor} /></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, fontFamily: 'monospace', letterSpacing: '0.02em' }}>
                            {formatValue(po.customer_po_number)}
                          </Typography>
                          {po.revision_count > 1 ? (
                            <Chip label="Revised" size="small" sx={{
                              height: 16, fontSize: '0.62rem', fontWeight: 700,
                              bgcolor: 'rgba(245,158,11,0.15)', color: '#d97706',
                              border: '1px solid rgba(245,158,11,0.35)',
                              '& .MuiChip-label': { px: 0.75 }
                            }} />
                          ) : (
                            <Chip label="New" size="small" sx={{
                              height: 16, fontSize: '0.62rem', fontWeight: 700,
                              bgcolor: 'rgba(16,185,129,0.12)', color: '#059669',
                              border: '1px solid rgba(16,185,129,0.3)',
                              '& .MuiChip-label': { px: 0.75 }
                            }} />
                          )}
                          {po.duplicate_count > 0 && po.revision_count <= 1 && (
                            <Tooltip title={`${po.duplicate_count} duplicate upload attempt${po.duplicate_count !== 1 ? 's' : ''} detected`}>
                              <Chip label="Duplicate" size="small" sx={{
                                height: 16, fontSize: '0.62rem', fontWeight: 700,
                                bgcolor: 'rgba(239,68,68,0.12)', color: '#dc2626',
                                border: '1px solid rgba(239,68,68,0.3)',
                                '& .MuiChip-label': { px: 0.75 },
                                cursor: 'default',
                              }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {po.status && <StatusChip status={po.status} />}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                          {formatValue(po.initial_order_date)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: isDark ? '#94a3b8' : '#64748b' }}>
                          {formatValue(po.po_version_date)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>
                        {formatValue(po.ship_to)}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>{formatValue(po.vendor_name_number)}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                          {/* Primary: Audit/Review button — always visible */}
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => navigate(`/audit/${po.id}`)}
                            startIcon={<Edit sx={{ fontSize: 13 }} />}
                            sx={{
                              fontSize: '0.73rem',
                              py: 0.5,
                              px: 1.25,
                              background: 'linear-gradient(135deg, #6366f1, #818cf8)',
                              boxShadow: 'none',
                              '&:hover': {
                                background: 'linear-gradient(135deg, #4f46e5, #6366f1)',
                                transform: 'translateY(-1px)',
                                boxShadow: '0 4px 12px rgba(99,102,241,0.35)',
                              },
                              whiteSpace: 'nowrap',
                            }}
                          >
                            Review
                          </Button>

                          {/* Validate against Bamboo Rose */}
                          <Tooltip title="Validate against Bamboo Rose data">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleValidate(po.id)}
                              startIcon={<VerifiedUser sx={{ fontSize: 13 }} />}
                              sx={{
                                fontSize: '0.73rem',
                                py: 0.5,
                                px: 1,
                                borderColor: 'rgba(245,158,11,0.4)',
                                color: '#d97706',
                                whiteSpace: 'nowrap',
                                '&:hover': {
                                  borderColor: '#f59e0b',
                                  backgroundColor: 'rgba(245,158,11,0.06)',
                                },
                              }}
                            >
                              Validate
                            </Button>
                          </Tooltip>

                          {/* Secondary: PDF icon buttons */}
                          {po.document_id && (
                            <>
                              <Tooltip title="View PDF">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewPDF(po.id)}
                                  sx={{
                                    width: 28, height: 28, borderRadius: '7px',
                                    color: '#ef4444',
                                    '&:hover': { backgroundColor: 'rgba(239,68,68,0.1)' },
                                  }}
                                >
                                  <PictureAsPdf sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="View Annotated PDF">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewAnnotatedPDF(po.id)}
                                  sx={{
                                    width: 28, height: 28, borderRadius: '7px',
                                    color: '#06b6d4',
                                    '&:hover': { backgroundColor: 'rgba(6,182,212,0.1)' },
                                  }}
                                >
                                  <Visibility sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>

                    {isExpanded && lineItems.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={12} sx={{ py: 0, px: 0, borderBottom: 0 }}>
                          <Box
                            sx={{
                              mx: 2,
                              mb: 2,
                              mt: 1,
                              borderRadius: '10px',
                              border: `1px solid ${isDark ? 'rgba(99,102,241,0.2)' : 'rgba(79,70,229,0.12)'}`,
                              overflow: 'hidden',
                              backgroundColor: isDark ? 'rgba(99,102,241,0.04)' : 'rgba(79,70,229,0.02)',
                            }}
                          >
                            <Box sx={{ p: 2, borderBottom: `1px solid ${isDark ? 'rgba(99,102,241,0.15)' : 'rgba(79,70,229,0.1)'}` }}>
                              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: isDark ? '#818cf8' : '#4f46e5' }}>
                                Line Items ({lineItems.length})
                              </Typography>
                            </Box>
                            <Box sx={{ overflowX: 'auto' }}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    {lineItemHeaders.map((h) => (
                                      <TableCell key={h.key} sx={{ whiteSpace: 'nowrap', backgroundColor: isDark ? 'rgba(99,102,241,0.07)' : 'rgba(79,70,229,0.04)' }}>
                                        {h.label}
                                      </TableCell>
                                    ))}
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {lineItems.map((item, idx) => (
                                    <TableRow key={item.id || idx}>
                                      {lineItemHeaders.map((h) => (
                                        <TableCell key={h.key} sx={{ whiteSpace: 'nowrap', backgroundColor: 'transparent' }}>
                                          {formatValue(item[h.key])}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{
            borderTop: `1px solid ${isDark ? 'rgba(148,163,184,0.07)' : 'rgba(15,23,42,0.06)'}`,
            '& .MuiTablePagination-toolbar': { fontSize: '0.8125rem' },
            '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.8125rem' },
          }}
        />
      </TableContainer>

      {/* PDF Viewer Modal */}
      <Dialog
        open={pdfModalOpen}
        onClose={handleClosePdfModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh', maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ borderBottom: `1px solid ${isDark ? 'rgba(148,163,184,0.08)' : 'rgba(15,23,42,0.07)'}`, pb: 1.5, pt: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">PDF Document — PO #{selectedPoId}</Typography>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {selectedPoId && (
                <>
                  <Tooltip title="Download PDF">
                    <IconButton size="small" onClick={() => handleDownloadPDF(selectedPoId)} sx={{ color: '#ef4444' }}>
                      <Download fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Annotated PDF">
                    <IconButton size="small" onClick={() => handleDownloadAnnotatedPDF(selectedPoId)} sx={{ color: '#06b6d4' }}>
                      <Download fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Excel">
                    <IconButton size="small" onClick={() => handleDownloadExcel(selectedPoId)} sx={{ color: '#10b981' }}>
                      <Download fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <IconButton size="small" onClick={handleClosePdfModal}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: '100%', overflow: 'hidden' }}>
          {pdfLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress sx={{ color: '#6366f1' }} />
            </Box>
          ) : pdfUrl ? (
            <iframe
              src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
              style={{ width: '100%', height: 'calc(90vh - 80px)', border: 'none' }}
              title="PDF Document Viewer"
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Typography sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '0.875rem' }}>No PDF available</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: `1px solid ${isDark ? 'rgba(148,163,184,0.08)' : 'rgba(15,23,42,0.07)'}`, gap: 1, p: 1.5 }}>
          {selectedPoId && (
            <>
              <Button size="small" startIcon={<Download />} onClick={() => handleDownloadPDF(selectedPoId)} variant="outlined" color="error">
                PDF
              </Button>
              <Button size="small" startIcon={<Download />} onClick={() => handleDownloadAnnotatedPDF(selectedPoId)} variant="outlined" color="secondary">
                Annotated
              </Button>
              <Button size="small" startIcon={<Download />} onClick={() => handleDownloadExcel(selectedPoId)} variant="outlined" color="success">
                Excel
              </Button>
            </>
          )}
          <Button size="small" onClick={handleClosePdfModal} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Bamboo Rose Validation Dialog ──────────────────────────────────── */}
      <Dialog
        open={validateDialogOpen}
        onClose={() => { if (!applying) setValidateDialogOpen(false); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <VerifiedUser sx={{ color: '#d97706', fontSize: 26 }} />
            <Box>
              <Typography variant="h6" fontWeight={700}>Bamboo Rose Validation</Typography>
              {validateData?.po_number && (
                <Typography variant="caption" color="text.secondary">
                  PO {validateData.po_number}
                  {validateData.extracted_at && ` · BR data from ${new Date(validateData.extracted_at).toLocaleString()}`}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {validateLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6, gap: 2 }}>
              <CircularProgress size={32} sx={{ color: '#d97706' }} />
              <Typography color="text.secondary">Comparing with Bamboo Rose data…</Typography>
            </Box>
          )}

          {!validateLoading && validateData?.error && (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">{validateData.error}</Alert>
            </Box>
          )}

          {!validateLoading && validateData && !validateData.error && !validateData.has_br_data && (
            <Box sx={{ p: 3 }}>
              <Alert severity="warning" icon={<WarningAmber />}>
                No Bamboo Rose data found for this PO.
              </Alert>
            </Box>
          )}

          {!validateLoading && validateData?.has_br_data && !validateData.br_found && (
            <Box sx={{ p: 3 }}>
              <Alert severity="info">
                PO <strong>{validateData.po_number}</strong> is not in an open state in the Bamboo Rose.
              </Alert>
            </Box>
          )}

          {!validateLoading && validateData?.br_found && (
            <Box>
              {/* Summary bar */}
              <Box sx={{ px: 3, py: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'action.hover' }}>
                {validateData.exact_match ? (
                  <Alert severity="success" icon={<CheckCircle />} sx={{ flex: 1, py: 0.5 }}>
                    All fields match Bamboo Rose exactly — no corrections needed.
                  </Alert>
                ) : (
                  <>
                    <Chip size="small" label={`${validateData.header_variances?.length || 0} header variance(s)`} color="warning" variant="outlined" />
                    <Chip size="small" label={`${validateData.line_item_variances?.length || 0} line row(s) with differences`} color="warning" variant="outlined" />
                    <Chip size="small" label={`${validateData.total_variances} total field(s) differ`} color="error" variant="outlined" sx={{ ml: 'auto' }} />
                  </>
                )}
              </Box>

              {applyResult && (
                <Box sx={{ px: 3, pt: 2 }}>
                  <Alert severity={applyResult.success ? 'success' : 'error'}>
                    {applyResult.success
                      ? `Applied: ${applyResult.header_fields_updated} header field(s), ${applyResult.line_items_updated} line item row(s) updated.`
                      : applyResult.message}
                  </Alert>
                </Box>
              )}

              {!validateData.exact_match && (
                <Box sx={{ px: 3, py: 2 }}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mb: 2 }}>
                    Check the boxes next to each suggested correction you want to accept. Unchecked items will be left as-is. Then click <strong>Apply Accepted</strong>.
                  </Typography>

                  {/* ── Header variances ───────────────────────────────────── */}
                  {validateData.header_variances?.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', mb: 1, color: 'text.primary' }}>
                        Header Fields
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ backgroundColor: 'action.hover' }}>
                              <TableCell sx={{ width: 40, fontWeight: 700 }}>Accept</TableCell>
                              <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: '#ef4444' }}>Our Value</TableCell>
                              <TableCell sx={{ fontWeight: 700, color: '#d97706' }}>Bamboo Rose Value</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {validateData.header_variances.map((v) => {
                              const key = `header:${v.field}`;
                              return (
                                <TableRow key={key} sx={{ backgroundColor: accepted[key] ? 'rgba(245,158,11,0.04)' : 'transparent' }}>
                                  <TableCell>
                                    <input
                                      type="checkbox"
                                      checked={!!accepted[key]}
                                      onChange={e => setAccepted(prev => ({ ...prev, [key]: e.target.checked }))}
                                      style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#d97706' }}
                                    />
                                  </TableCell>
                                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{v.label}</TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'inline-block', bgcolor: 'rgba(239,68,68,0.15)', color: '#ef4444', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.78rem', fontFamily: 'monospace' }}>
                                      {v.our_value ?? <em style={{ opacity: 0.6 }}>empty</em>}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Box sx={{ display: 'inline-block', bgcolor: 'rgba(245,158,11,0.15)', color: '#d97706', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.78rem', fontFamily: 'monospace', fontWeight: 600 }}>
                                      {v.br_value ?? <em style={{ opacity: 0.6 }}>empty</em>}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}

                  {/* ── Line item variances ─────────────────────────────────── */}
                  {validateData.line_item_variances?.length > 0 && (
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', mb: 1, color: 'text.primary' }}>
                        Line Items
                      </Typography>
                      {validateData.line_item_variances.map((row) => (
                        <Box key={row.line_item_id} sx={{ mb: 2 }}>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                            Row {row.row_number} — {row.identifier}
                          </Typography>
                          <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                  <TableCell sx={{ width: 40, fontWeight: 700 }}>Accept</TableCell>
                                  <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: '#ef4444' }}>Our Value</TableCell>
                                  <TableCell sx={{ fontWeight: 700, color: '#d97706' }}>Bamboo Rose Value</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {row.variances.map((v) => {
                                  const key = `line:${row.line_item_id}:${v.field}`;
                                  return (
                                    <TableRow key={key} sx={{ backgroundColor: accepted[key] ? 'rgba(245,158,11,0.04)' : 'transparent' }}>
                                      <TableCell>
                                        <input
                                          type="checkbox"
                                          checked={!!accepted[key]}
                                          onChange={e => setAccepted(prev => ({ ...prev, [key]: e.target.checked }))}
                                          style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#d97706' }}
                                        />
                                      </TableCell>
                                      <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary' }}>{v.label}</TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'inline-block', bgcolor: 'rgba(239,68,68,0.15)', color: '#ef4444', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.78rem', fontFamily: 'monospace' }}>
                                          {v.our_value ?? <em style={{ opacity: 0.6 }}>empty</em>}
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Box sx={{ display: 'inline-block', bgcolor: 'rgba(245,158,11,0.15)', color: '#d97706', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.78rem', fontFamily: 'monospace', fontWeight: 600 }}>
                                          {v.br_value ?? <em style={{ opacity: 0.6 }}>empty</em>}
                                        </Box>
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button variant="outlined" onClick={() => setValidateDialogOpen(false)} disabled={applying}>
            Close
          </Button>
          {validateData?.br_found && !validateData.exact_match && !applyResult?.success && (
            <>
              <Button
                variant="text"
                size="small"
                onClick={() => {
                  const allKeys = {};
                  Object.keys(accepted).forEach(k => { allKeys[k] = true; });
                  setAccepted(allKeys);
                }}
              >
                Select All
              </Button>
              <Button
                variant="contained"
                color="warning"
                disabled={applying || !Object.values(accepted).some(Boolean)}
                startIcon={applying ? <CircularProgress size={14} color="inherit" /> : <VerifiedUser />}
                onClick={handleApplyAccepted}
              >
                {applying ? 'Applying…' : 'Apply Accepted'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
